/**
 * SYM-6185. What pressing the video entry on an ad means.
 *
 * Steven, 19 September 2026: the sparkle on a video ad reopens THAT video.
 * Whoever wants a fresh one makes a new ad, where the empty ad offers
 * "Generate a video using AI". So there is one rule and no second button:
 *
 * - an ad that carries a video -> open that video again;
 * - anything else -> ask for a fresh proposal, as before.
 *
 * A Bring to Life clip is the exception (Steven, 19 September 2026): it has no
 * slides to open, so its sparkle is greyed out and says so. That wins over
 * everything else here, so no route into the wizard exists from such an ad.
 *
 * Pictures picked at the "not enough pictures" refusal always mean a fresh
 * proposal: they are an answer to a question only a new video asks.
 */
export type WizardEntry =
  | { kind: 'fresh' }
  | { kind: 'bring-to-life' }
  | { kind: 'reopen'; resourceId: number };

export interface WizardEntryInput {
  /** True when the ad's creative is a video. */
  adIsVideo: boolean;
  /** True when that video is a Bring to Life clip. */
  adIsBringToLife?: boolean | null;
  /** The id of the file on the ad, when it has one. */
  resourceId?: number | null;
  /** Pictures the customer picked at the refusal, if that is how we got here. */
  pickedPictureIds?: number[] | null;
}

export function wizardEntryFor({
  adIsVideo,
  adIsBringToLife,
  resourceId,
  pickedPictureIds,
}: WizardEntryInput): WizardEntry {
  const picked = Array.isArray(pickedPictureIds) && pickedPictureIds.length > 0;
  const hasFile =
    typeof resourceId === 'number' &&
    Number.isInteger(resourceId) &&
    resourceId > 0;

  if (adIsVideo && adIsBringToLife === true) return { kind: 'bring-to-life' };

  return adIsVideo && hasFile && !picked
    ? { kind: 'reopen', resourceId: resourceId as number }
    : { kind: 'fresh' };
}

/** What to tell the customer when a video cannot be opened again. */
export const CANNOT_REOPEN =
  'This video cannot be opened again: what it was made from is no longer there. To make a new one, add a new ad and choose "Generate a video using AI".';

/** Why the sparkle is greyed out on a Bring to Life clip. Steven's words. */
export const CANNOT_EDIT_BRING_TO_LIFE =
  'You cannot edit a Bring to Life video. You should make a new one.';

type MarkedAd = {
  bring_to_life_resource_id?: number | null;
  path?: { id?: number | null } | null;
};

/**
 * Whether the video on this ad is a Bring to Life clip.
 *
 * A mark says something about a FILE, and that stays true whatever happens to
 * the ad it was made on. So the question is asked of the file the ad carries
 * NOW, against every mark the editor holds (`ads` = all ads of the campaign):
 * - a blank ad copied from a Bring to Life ad has no file: no;
 * - a video that replaced the clip is another file: no;
 * - the same clip picked from the library onto ANOTHER ad: yes, although that
 *   ad was never marked itself (gate review, 19 Sep 2026).
 */
export function carriesBringToLifeClip(
  ad: MarkedAd | null | undefined,
  ads: ReadonlyArray<MarkedAd | null | undefined> = []
): boolean {
  const file = ad?.path?.id;
  if (typeof file !== 'number' || file <= 0) return false;

  return [ad, ...ads].some(
    (other) => other?.bring_to_life_resource_id === file
  );
}

/** The mark for an ad as the server sent it: its yes/no, tied to its file. */
export function bringToLifeMarkFrom(ad: {
  is_bring_to_life_clip?: boolean | null;
  path?: { id?: number | null } | null;
}): number | null {
  return ad.is_bring_to_life_clip === true && typeof ad.path?.id === 'number'
    ? ad.path.id
    : null;
}
